import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { finishingApi, repairsApi } from '../api/production';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '@mui/material/Avatar';

interface CompleteDesign {
  design_no: string;
  barcoded_qty: number;
  damaged_qty: number;
  total_qty: number;
  image_url?: string | null;
  latest_qc_date?: string | null;
}

interface BreakdownRecord {
  batch_id?: number;
  record_id?: number;
  batch_number: string;
  date: string;
  datetime: string;
  qty: number;
  type: string;
  garment_part?: string | null;
  good_qty?: number;
  damage_qty?: number;
  permanent_damage_qty?: number;
  processed_by?: string;
  notes?: string | null;
  size_breakdown?: Array<{
    size: string;
    qty?: number;
    qc_qty?: number;
    barcoded_qty?: number;
    damaged_qty?: number;
  }>;
  qc_qty?: number;
  barcoded_qty?: number;
  damaged_qty?: number;
}

interface RepairRecord {
  id: number;
  batch_number: string;
  garment_part?: string | null;
  size: string;
  quantity: number;
  repair_type: string;
  repair_status: string;
  assigned_to?: string | null;
  reported_at: string;
  repair_started_at?: string | null;
  repair_completed_at?: string | null;
  return_to_stage: string;
  date: string;
  datetime: string;
  damage_notes?: string | null;
}

interface DesignBreakdown {
  design_no: string;
  totals: {
    opening_entries_qc: number;
    opening_entries_barcoded: number;
    opening_entries_damaged: number;
    qc_total: number;
    barcoded_total: number;
    damaged_total: number;
    grand_total: number;
    repair_total: number;
  };
  opening_entries: BreakdownRecord[];
  qc_records: BreakdownRecord[];
  barcoded_records: BreakdownRecord[];
  damaged_records: BreakdownRecord[];
  repair_records: RepairRecord[];
}

export default function CompleteDesignsPage() {
  const { user: authUser } = useAuth();
  const isMaster = authUser?.role === 'MASTER';

  const [designs, setDesigns] = useState<CompleteDesign[]>([]);
  const [filteredDesigns, setFilteredDesigns] = useState<CompleteDesign[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownData, setBreakdownData] = useState<DesignBreakdown | null>(null);
  const [selectedDesignNo, setSelectedDesignNo] = useState<string | null>(null);
  const [breakdownTab, setBreakdownTab] = useState(0);

  // Edit state
  const [editingRow, setEditingRow] = useState<{ tab: string; index: number } | null>(null);
  const [editValues, setEditValues] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCompleteDesigns();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = designs.filter(design =>
        design.design_no.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredDesigns(filtered);
    } else {
      setFilteredDesigns(designs);
    }
  }, [designs, searchQuery]);

  const fetchCompleteDesigns = async () => {
    try {
      setLoading(true);
      const data = await finishingApi.getCompleteDesigns();
      setDesigns(data);
    } catch (err: any) {
      console.error('Error fetching complete designs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTotalClick = async (designNo: string) => {
    setSelectedDesignNo(designNo);
    setBreakdownDialogOpen(true);
    setBreakdownLoading(true);
    setBreakdownTab(0);
    setEditingRow(null);

    try {
      const data = await finishingApi.getDesignBreakdown(designNo);
      setBreakdownData(data);
    } catch (err: any) {
      console.error('Error fetching design breakdown:', err);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const handleCloseBreakdown = () => {
    setBreakdownDialogOpen(false);
    setBreakdownData(null);
    setSelectedDesignNo(null);
    setBreakdownTab(0);
    setEditingRow(null);
  };

  const refreshBreakdown = async () => {
    if (!selectedDesignNo) return;
    try {
      const data = await finishingApi.getDesignBreakdown(selectedDesignNo);
      setBreakdownData(data);
    } catch (err: any) {
      console.error('Error refreshing breakdown:', err);
    }
  };

  // --- Edit handlers ---
  const startEditOpeningEntry = (idx: number, entry: BreakdownRecord) => {
    setEditingRow({ tab: 'opening', index: idx });
    setEditValues({
      qc_qty: entry.qc_qty || 0,
      barcoded_qty: entry.barcoded_qty || 0,
      damaged_qty: entry.damaged_qty || 0,
    });
  };

  const startEditQcRecord = (idx: number, record: BreakdownRecord) => {
    setEditingRow({ tab: 'qc', index: idx });
    setEditValues({
      good_qty: record.good_qty || 0,
      damage_qty: record.damage_qty || 0,
      permanent_damage_qty: record.permanent_damage_qty || 0,
    });
  };

  const startEditBarcoded = (idx: number, record: BreakdownRecord) => {
    setEditingRow({ tab: 'barcoded', index: idx });
    setEditValues({ barcoded_qty: record.qty || 0 });
  };

  const startEditDamaged = (idx: number, record: BreakdownRecord) => {
    setEditingRow({ tab: 'damaged', index: idx });
    setEditValues({ damaged_qty: record.qty || 0 });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const saveEdit = async () => {
    if (!editingRow || !breakdownData) return;
    setSaving(true);
    try {
      // QC record edit uses a different API
      if (editingRow.tab === 'qc') {
        const record = breakdownData.qc_records[editingRow.index];
        if (!record?.record_id) {
          setSnackbar({ open: true, message: 'Cannot edit: record ID missing', severity: 'error' });
          return;
        }
        await finishingApi.editQcRecord(record.record_id, {
          good_qty: editValues.good_qty || 0,
          damage_qty: editValues.damage_qty || 0,
          permanent_damage_qty: editValues.permanent_damage_qty || 0,
        });
        setSnackbar({ open: true, message: 'QC record updated successfully', severity: 'success' });
        setEditingRow(null);
        setEditValues({});
        await refreshBreakdown();
        fetchCompleteDesigns();
        return;
      }

      // Batch breakdown edit for other tabs
      let record: BreakdownRecord | undefined;
      if (editingRow.tab === 'opening') {
        record = breakdownData.opening_entries[editingRow.index];
      } else if (editingRow.tab === 'barcoded') {
        record = breakdownData.barcoded_records[editingRow.index];
      } else if (editingRow.tab === 'damaged') {
        record = breakdownData.damaged_records[editingRow.index];
      }

      if (!record?.batch_id) {
        setSnackbar({ open: true, message: 'Cannot edit: batch ID missing', severity: 'error' });
        return;
      }

      const payload: Record<string, number> = {};
      if (editingRow.tab === 'opening') {
        if (editValues.qc_qty !== undefined) payload.qc_qty = editValues.qc_qty;
        if (editValues.barcoded_qty !== undefined) payload.barcoded_qty = editValues.barcoded_qty;
        if (editValues.damaged_qty !== undefined) payload.damaged_qty = editValues.damaged_qty;
      } else if (editingRow.tab === 'barcoded') {
        payload.barcoded_qty = editValues.barcoded_qty;
      } else if (editingRow.tab === 'damaged') {
        payload.damaged_qty = editValues.damaged_qty;
      }

      await finishingApi.editBatchBreakdown(record.batch_id, payload);
      setSnackbar({ open: true, message: 'Quantities updated successfully', severity: 'success' });
      setEditingRow(null);
      setEditValues({});
      await refreshBreakdown();
      fetchCompleteDesigns();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to update', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteQcRecord = async (record: BreakdownRecord) => {
    if (!record.record_id) return;
    if (!window.confirm(`Delete this QC record? This will reverse ${record.qty} pieces (Good: ${record.good_qty || 0}, Damage: ${record.damage_qty || 0}, Perm: ${record.permanent_damage_qty || 0}).`)) {
      return;
    }
    setSaving(true);
    try {
      await finishingApi.deleteQcRecord(record.record_id);
      setSnackbar({ open: true, message: 'QC record deleted and quantities reversed', severity: 'success' });
      await refreshBreakdown();
      fetchCompleteDesigns();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to delete', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const startEditRepair = (idx: number, record: RepairRecord) => {
    setEditingRow({ tab: 'repair', index: idx });
    setEditValues({ quantity: record.quantity });
  };

  const saveRepairEdit = async () => {
    if (!editingRow || !breakdownData) return;
    const record = breakdownData.repair_records[editingRow.index];
    if (!record?.id) return;
    setSaving(true);
    try {
      await repairsApi.editRepairItem(record.id, { quantity: editValues.quantity || 1 });
      setSnackbar({ open: true, message: 'Repair item updated', severity: 'success' });
      setEditingRow(null);
      setEditValues({});
      await refreshBreakdown();
      fetchCompleteDesigns();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to update repair item', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteRepairItem = async (record: RepairRecord) => {
    if (!record.id) return;
    if (!window.confirm(`Delete this repair item? This will reverse ${record.quantity} pcs (${record.repair_type}, Size: ${record.size}) back to pipeline.`)) {
      return;
    }
    setSaving(true);
    try {
      await repairsApi.deleteRepairItem(record.id);
      setSnackbar({ open: true, message: 'Repair item deleted and quantities reversed', severity: 'success' });
      await refreshBreakdown();
      fetchCompleteDesigns();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to delete repair item', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isEditing = (tab: string, idx: number) =>
    editingRow?.tab === tab && editingRow?.index === idx;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Complete Designs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View designs with barcoded and damaged quantities
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by design number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Designs
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {filteredDesigns.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Barcoded
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {filteredDesigns.reduce((sum, d) => sum + d.barcoded_qty, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Damaged
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {filteredDesigns.reduce((sum, d) => sum + d.damaged_qty, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Designs Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredDesigns.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {searchQuery ? 'No designs found matching your search' : 'No complete designs found'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredDesigns.map((design) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={design.design_no}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
                    {design.image_url ? (
                      <CardMedia
                        component="img"
                        image={design.image_url}
                        alt={design.design_no}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200'
                        }}
                      >
                        <Avatar sx={{ width: 60, height: 60, fontSize: '1.5rem' }}>
                          {design.design_no.substring(0, 2)}
                        </Avatar>
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {design.design_no}
                    </Typography>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" color="white">
                            {design.barcoded_qty}
                          </Typography>
                          <Typography variant="caption" color="white">
                            Barcoded
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                          <Typography variant="h6" color="white">
                            {design.damaged_qty}
                          </Typography>
                          <Typography variant="caption" color="white">
                            Damaged
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            textAlign: 'center',
                            p: 1,
                            bgcolor: 'primary.light',
                            borderRadius: 1,
                            mt: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s'
                            }
                          }}
                          onClick={() => handleTotalClick(design.design_no)}
                        >
                          <Typography variant="h6" color="white">
                            {design.total_qty}
                          </Typography>
                          <Typography variant="caption" color="white">
                            Total (Click for breakdown)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Breakdown Dialog */}
      <Dialog
        open={breakdownDialogOpen}
        onClose={handleCloseBreakdown}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Design Breakdown: {selectedDesignNo}
        </DialogTitle>
        <DialogContent>
          {breakdownLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : breakdownData ? (
            <Box>
              {/* Summary Totals */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md>
                  <Paper sx={{ p: 2, bgcolor: 'info.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="white" display="block">
                      Opening Entries - QC
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {breakdownData.totals.opening_entries_qc}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="white" display="block">
                      Barcoded Total
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {breakdownData.totals.barcoded_total}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <Paper sx={{ p: 2, bgcolor: 'error.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="white" display="block">
                      Damaged Total
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {breakdownData.totals.damaged_total}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <Paper sx={{ p: 2, bgcolor: 'warning.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="white" display="block">
                      In Repair
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {breakdownData.totals.repair_total}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <Paper sx={{ p: 2, bgcolor: 'primary.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="white" display="block">
                      Grand Total
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="bold">
                      {breakdownData.totals.grand_total}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Tabs for different record types */}
              <Tabs value={breakdownTab} onChange={(_, v) => { setBreakdownTab(v); cancelEdit(); }} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
                <Tab label={`Opening Entries (${breakdownData.opening_entries.length})`} />
                <Tab label={`QC Records (${breakdownData.qc_records.length})`} />
                <Tab label={`Barcoded (${breakdownData.barcoded_records.length})`} />
                <Tab label={`Damaged (${breakdownData.damaged_records.length})`} />
                <Tab label={`Repair Queue (${breakdownData.repair_records?.length || 0})`} />
              </Tabs>

              {/* Opening Entries Tab */}
              {breakdownTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell align="right">QC Qty</TableCell>
                        <TableCell align="right">Barcoded Qty</TableCell>
                        <TableCell align="right">Damaged Qty</TableCell>
                        {isMaster && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breakdownData.opening_entries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMaster ? 7 : 6} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No opening entry records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        breakdownData.opening_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{entry.date}</TableCell>
                            <TableCell>{entry.batch_number}</TableCell>
                            <TableCell>
                              {entry.garment_part ? (
                                <Chip label={entry.garment_part} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {isEditing('opening', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.qc_qty ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, qc_qty: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              ) : (entry.qc_qty || 0)}
                            </TableCell>
                            <TableCell align="right">
                              {isEditing('opening', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.barcoded_qty ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, barcoded_qty: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              ) : (entry.barcoded_qty || 0)}
                            </TableCell>
                            <TableCell align="right">
                              {isEditing('opening', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.damaged_qty ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, damaged_qty: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              ) : (entry.damaged_qty || 0)}
                            </TableCell>
                            {isMaster && (
                              <TableCell align="center">
                                {isEditing('opening', idx) ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={cancelEdit}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <IconButton size="small" onClick={() => startEditOpeningEntry(idx, entry)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* QC Records Tab */}
              {breakdownTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Good (Barcode)</TableCell>
                        <TableCell align="right">Damage (For Repair)</TableCell>
                        <TableCell align="right">Perm. Damage</TableCell>
                        <TableCell>By</TableCell>
                        <TableCell>Notes</TableCell>
                        {isMaster && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breakdownData.qc_records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMaster ? 10 : 9} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No QC records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        breakdownData.qc_records.map((record, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.type}
                                size="small"
                                color={record.type === 'Opening Entry' ? 'info' : 'success'}
                              />
                            </TableCell>
                            <TableCell>
                              {record.garment_part ? (
                                <Chip label={record.garment_part} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {isEditing('qc', idx)
                                ? (editValues.good_qty || 0) + (editValues.damage_qty || 0) + (editValues.permanent_damage_qty || 0)
                                : record.qty}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {record.type === 'QC Processing' ? (
                                isEditing('qc', idx) ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editValues.good_qty ?? 0}
                                    onChange={(e) => setEditValues(v => ({ ...v, good_qty: parseInt(e.target.value) || 0 }))}
                                    sx={{ width: 70 }}
                                    inputProps={{ min: 0 }}
                                  />
                                ) : (record.good_qty || 0)
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                              {record.type === 'QC Processing' ? (
                                isEditing('qc', idx) ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editValues.damage_qty ?? 0}
                                    onChange={(e) => setEditValues(v => ({ ...v, damage_qty: parseInt(e.target.value) || 0 }))}
                                    sx={{ width: 70 }}
                                    inputProps={{ min: 0 }}
                                  />
                                ) : (record.damage_qty || 0)
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              {record.type === 'QC Processing' ? (
                                isEditing('qc', idx) ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editValues.permanent_damage_qty ?? 0}
                                    onChange={(e) => setEditValues(v => ({ ...v, permanent_damage_qty: parseInt(e.target.value) || 0 }))}
                                    sx={{ width: 70 }}
                                    inputProps={{ min: 0 }}
                                  />
                                ) : (record.permanent_damage_qty || 0)
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{record.processed_by || '-'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{record.notes || '-'}</Typography>
                            </TableCell>
                            {isMaster && (
                              <TableCell align="center">
                                {record.type === 'QC Processing' && record.record_id && (
                                  isEditing('qc', idx) ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      <IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}>
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={cancelEdit}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      <IconButton size="small" onClick={() => startEditQcRecord(idx, record)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => deleteQcRecord(record)}
                                        disabled={saving}
                                        title="Delete QC record and reverse quantities"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Barcoded Records Tab */}
              {breakdownTab === 2 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell align="right">Barcoded Qty</TableCell>
                        {isMaster && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breakdownData.barcoded_records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMaster ? 6 : 5} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No barcoded records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        breakdownData.barcoded_records.map((record, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.batch_number}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.type}
                                size="small"
                                color={record.type === 'Opening Entry' ? 'info' : 'success'}
                              />
                            </TableCell>
                            <TableCell>
                              {record.garment_part ? (
                                <Chip label={record.garment_part} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {isEditing('barcoded', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.barcoded_qty ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, barcoded_qty: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              ) : record.qty}
                            </TableCell>
                            {isMaster && (
                              <TableCell align="center">
                                {isEditing('barcoded', idx) ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={cancelEdit}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <IconButton size="small" onClick={() => startEditBarcoded(idx, record)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Damaged Records Tab */}
              {breakdownTab === 3 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell align="right">Damaged Qty</TableCell>
                        {isMaster && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breakdownData.damaged_records.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMaster ? 6 : 5} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No damaged records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        breakdownData.damaged_records.map((record, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.batch_number}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.type}
                                size="small"
                                color={record.type === 'Opening Entry' ? 'info' : 'error'}
                              />
                            </TableCell>
                            <TableCell>
                              {record.garment_part ? (
                                <Chip label={record.garment_part} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {isEditing('damaged', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.damaged_qty ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, damaged_qty: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              ) : record.qty}
                            </TableCell>
                            {isMaster && (
                              <TableCell align="center">
                                {isEditing('damaged', idx) ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={cancelEdit}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <IconButton size="small" onClick={() => startEditDamaged(idx, record)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Repair Queue Tab */}
              {breakdownTab === 4 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Repair Type</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Return To</TableCell>
                        {isMaster && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!breakdownData.repair_records || breakdownData.repair_records.length === 0) ? (
                        <TableRow>
                          <TableCell colSpan={isMaster ? 10 : 9} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No repair records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        breakdownData.repair_records.map((record, idx) => (
                          <TableRow key={record.id}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.batch_number}</TableCell>
                            <TableCell>
                              {record.garment_part ? (
                                <Chip label={record.garment_part} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip label={record.size} size="small" />
                            </TableCell>
                            <TableCell>{record.repair_type}</TableCell>
                            <TableCell align="right">
                              {isEditing('repair', idx) ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValues.quantity ?? 0}
                                  onChange={(e) => setEditValues(v => ({ ...v, quantity: parseInt(e.target.value) || 0 }))}
                                  sx={{ width: 70 }}
                                  inputProps={{ min: 1 }}
                                />
                              ) : (
                                <Typography variant="body2" fontWeight="bold">{record.quantity}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={record.repair_status}
                                size="small"
                                color={
                                  record.repair_status === 'COMPLETED' ? 'success' :
                                  record.repair_status === 'IN_PROGRESS' ? 'info' : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>{record.assigned_to || '-'}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {record.return_to_stage?.replace(/_/g, ' ') || '-'}
                              </Typography>
                            </TableCell>
                            {isMaster && (
                              <TableCell align="center">
                                {isEditing('repair', idx) ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton size="small" color="primary" onClick={saveRepairEdit} disabled={saving}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={cancelEdit}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <IconButton size="small" onClick={() => startEditRepair(idx, record)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteRepairItem(record)}
                                      disabled={saving}
                                      title="Delete repair item and reverse quantities"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBreakdown}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
